<script setup>
import dayjs from 'dayjs'
import { ref, computed, onMounted } from 'vue'

import { getI18n } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { infoBoxIdEnum, entityChangeTypeEnum } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'


import { useCamera } from '@/composables/camera'
import { useEntityChanges } from '@/composables/entity-changes'
import { useDetectDevice } from '@/composables/device'
import { useHighlightNewRow } from '@/composables/scroll'
import { useLockedCostReportRow } from '@/composables/cost-report'
import { groupExpensesByDate, isClaimableExpense, unclaimedExpenseParams, useExpenseClaimActions } from '@/composables/expense-claim'

import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import EditModeToggle from '@/components/shared/EditModeToggle.vue'
import InfoBox from '@/components/shared/InfoBox.vue'
import CostReportRowEdit from '@/components/accounting/CostReportRowEdit.vue'
import ExpenseDayHeader from '@/components/accounting/ExpenseDayHeader.vue'
import ExpenseRowListItem from '@/components/accounting/ExpenseRowListItem.vue'
import ExpenseClaimDialog from '@/components/accounting/ExpenseClaimDialog.vue'

const props = defineProps({
  staff: {
    type: Number,
    default: null,
  },
})

const i18n = getI18n()
const accountingStore = useAccountingStore()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const { isMobile } = useDetectDevice()
const { readImageAsync } = useCamera()
const { isLocked } = useLockedCostReportRow()
const { canCreate } = useExpenseClaimActions()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()
const { costReportRowTypeEnum } = accountingStore

const rows = ref([])
const isLoading = ref(false)
const editItem = ref(null)
const editMode = ref(false)
const rowToDelete = ref(null)
const cameraInput = ref(null)
const rowsContainer = ref(null)

const groups = computed(() => groupExpensesByDate(rows.value))

const selectMode = ref(false)
const selectedIds = ref([])
const claimableRows = computed(() => rows.value.filter(isClaimableExpense))
const selectedRows = computed(() => rows.value.filter(row => selectedIds.value.includes(row.id)))
const hasMixedStaff = computed(() => new Set(selectedRows.value.map(row => row.staff)).size > 1)
const selectionState = computed(() => {
  if (!selectedIds.value.length) return false
  return selectedIds.value.length === claimableRows.value.length ? true : null
})

const loadRowsAsync = async() => {
  rows.value = (await accountingStore.api.getCostReportRowsAsync({ ...unclaimedExpenseParams(), ...(props.staff && { staff: props.staff }) })).data
}

const loadAsync = async() => {
  if (!webSiteStore.hasStaff && !webSiteStore.canApproveExpenseClaims) return
  isLoading.value = true
  try {
    await Promise.all([loadRowsAsync(), webSiteStore.canApproveExpenseClaims && planningStore.fetchStaffsAsync({}, true)])
  } finally {
    isLoading.value = false
  }
}

const openNewExpense = (values = {}) => {
  editItem.value = { type: costReportRowTypeEnum.EXPENSE, ...(props.staff && { staff: props.staff }), ...values }
}
const onSelectModeChanged = (value) => {
  selectMode.value = value
  selectedIds.value = []
  if (value) editMode.value = false
}
const onEditModeChanged = (value) => {
  editMode.value = value
  if (value) onSelectModeChanged(false)
}
const toggleRowSelection = (row) => {
  if (!isClaimableExpense(row)) return
  selectedIds.value = selectedIds.value.includes(row.id) ? selectedIds.value.filter(id => id !== row.id) : [...selectedIds.value, row.id]
  if (!selectedIds.value.length) selectMode.value = false
}
const HOLD_CLICK_GUARD_MS = 500
let lastHoldAt = 0
const onRowHold = (row) => {
  if (selectMode.value || !isClaimableExpense(row)) return
  onSelectModeChanged(true)
  toggleRowSelection(row)
  lastHoldAt = Date.now()
}
const toggleAllRows = () => {
  selectedIds.value = selectionState.value === true ? [] : claimableRows.value.map(row => row.id)
}
const onAvatarClick = (row) => {
  if (!selectMode.value) onSelectModeChanged(true)
  toggleRowSelection(row)
}
const onRowClick = (row) => {
  if (Date.now() - lastHoldAt < HOLD_CLICK_GUARD_MS) return
  if (selectMode.value) toggleRowSelection(row)
  else editItem.value = { ...row }
}
const emit = defineEmits(['claim-saved'])

const newClaim = ref(null)
const preselectedRowIds = ref([])
const openNewClaim = () => {
  const staff = selectedRows.value[0]?.staff ?? props.staff ?? webSiteStore.staffId
  if (!canCreate(staff)) return
  preselectedRowIds.value = selectedRows.value.map(row => row.id)
  newClaim.value = { staff }
}
const onClaimSaved = async(claim) => {
  newClaim.value = null
  if (claim) onSelectModeChanged(false)
  await loadAsync()
  if (claim) emit('claim-saved', claim)
}

const openCamera = () => cameraInput.value?.click()
const onPhotoTaken = async(event) => {
  const image = await readImageAsync(event)
  if (!image) return
  const today = dayjs().format('YYYY-MM-DD')
  const todayProjects = await planningStore.fetchProjectsAsync({ staff: webSiteStore.staffId, date_from: today, date_to: today }) ?? []
  openNewExpense({ date: today, project: todayProjects[0]?.id ?? null, row_files: [{ name: image.name, payload: image.dataUrl }] })
}

const reloadHighlightingNewRowAsync = async() => {
  const previousIds = new Set(rows.value.map(row => row.id))
  await loadAsync()
  await highlightRowAsync(rows.value.find(row => !previousIds.has(row.id))?.id, rowsContainer)
}

const onRowSaved = async() => {
  const wasCreated = !editItem.value?.id
  editItem.value = null
  if (wasCreated) await reloadHighlightingNewRowAsync()
  else await loadAsync()
}

useEntityChanges([entityChangeTypeEnum.COST_REPORT_ROW], (change) => (change.created ? reloadHighlightingNewRowAsync() : loadAsync()), () => editItem.value !== null)

const onDeleteConfirmedAsync = async() => {
  await accountingStore.deleteCostReportRowAsync(rowToDelete.value.id)
  rowToDelete.value = null
  await loadAsync()
}

onMounted(() => Promise.all([
  accountingStore.fetchCostReportRowTagsAsync({}, true),
  loadAsync(),
]))

defineExpose({ openCamera, openNewExpense, openNewClaim })
</script>

<template>
  <div class="column no-wrap">
    <input
      ref="cameraInput"
      type="file"
      accept="image/*"
      capture="environment"
      class="hidden"
      @change="onPhotoTaken"
    >
    <confirm-dialog
      :model-value="rowToDelete !== null"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @update:model-value="value => { if (!value) rowToDelete = null }"
      @confirm="onDeleteConfirmedAsync"
    />
    <expense-claim-dialog
      :claim="newClaim"
      :preselected-row-ids="preselectedRowIds"
      @saved="onClaimSaved"
      @cancel="newClaim = null"
    />
    <q-dialog
      :model-value="editItem !== null"
      class="dialog-xs"
      :maximized="isMobile"
      persistent
    >
      <cost-report-row-edit
        v-if="editItem !== null"
        :model-value="editItem"
        @update="onRowSaved"
        @cancel="editItem = null"
      />
    </q-dialog>

    <info-box v-if="!webSiteStore.hasStaff && !webSiteStore.canApproveExpenseClaims" class="q-ma-sm" :message="i18n.t('error.validation.reportStaffNotLinked')" />
    <template v-else>
      <div class="row items-center no-wrap q-px-md q-py-xs">
        <template v-if="selectMode">
          <q-checkbox :model-value="selectionState" dense @update:model-value="toggleAllRows">
            <q-tooltip>{{ selectionState === true ? i18n.t('common.selectNone') : i18n.t('common.selectAll') }}</q-tooltip>
          </q-checkbox>
          <div class="text-caption text-grey-7 q-ml-sm">{{ selectedIds.length }} / {{ claimableRows.length }}</div>
        </template>
        <q-space />
        <div class="row items-center no-wrap q-gutter-x-sm">
          <q-btn
            v-if="claimableRows.length"
            flat
            round
            dense
            :icon="selectMode ? 'mdi-checkbox-multiple-marked' : 'mdi-checkbox-multiple-blank-outline'"
            :color="selectMode ? 'primary' : 'grey-6'"
            @click="onSelectModeChanged(!selectMode)"
          >
            <q-tooltip>{{ selectMode ? i18n.t('common.done') : i18n.t('common.select') }}</q-tooltip>
          </q-btn>
          <edit-mode-toggle v-if="rows.length" :model-value="editMode" @update:model-value="onEditModeChanged" />
          <q-btn
            v-if="rows.length"
            style="padding: 0;"
            size="md"
            unelevated
            ripple
            rounded
            icon="mdi-receipt-text-plus-outline"
            :disable="hasMixedStaff"
            @click="openNewClaim"
          >
            <q-badge
              v-if="selectedIds.length"
              color="primary"
              floating
              rounded
            >{{ selectedIds.length }}</q-badge>
            <q-tooltip>{{ i18n.t('common.expenseClaimNew') }}</q-tooltip>
          </q-btn>
          <q-btn
            style="padding: 0;"
            size="md"
            unelevated
            ripple
            rounded
            icon="mdi-plus-circle-outline"
            @click="openNewExpense()"
          />
        </div>
      </div>

      <q-inner-loading :showing="isLoading" />
      <div ref="rowsContainer" class="col scroll q-px-sm">
        <div v-for="group in groups" :key="group.date">
          <expense-day-header :date="group.date" :total="group.total" />
          <q-list>
            <q-item
              v-for="row in group.rows"
              :key="row.id"
              v-touch-hold.mouse="() => onRowHold(row)"
              class="q-pa-none expense-row"
              :class="{ 'new-row-highlight': row.id === highlightedRowId, 'selected-row': selectedIds.includes(row.id), 'dx-disabled-row': selectMode && !isClaimableExpense(row) }"
              clickable
              @click="onRowClick(row)"
            >
              <expense-row-list-item
                :row="row"
                :show-staff="webSiteStore.canApproveExpenseClaims"
                :selected="selectedIds.includes(row.id)"
                :avatar-selectable="isClaimableExpense(row)"
                class="full-width"
                @avatar-click="onAvatarClick(row)"
              >
                <template v-if="editMode && !isLocked(row)" #actions>
                  <q-btn
                    style="padding: 0;"
                    size="md"
                    flat
                    rounded
                    color="negative"
                    icon="mdi-minus-circle-outline"
                    @click.stop="rowToDelete = row"
                  />
                </template>
              </expense-row-list-item>
            </q-item>
          </q-list>
        </div>

        <div v-if="!isLoading && !rows.length" class="text-center text-grey-6 q-pa-lg">
          {{ i18n.t('common.noExpensesWithoutClaim') }}
        </div>
        <info-box :id="infoBoxIdEnum.MY_EXPENSES_HINT" class="q-ma-sm" :message="i18n.t('info.myExpensesHint')" />
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.expense-row {
  user-select: none;
  -webkit-touch-callout: none;
}
</style>
